import * as React from "react";
import { Dialog as SheetPrimitive } from "radix-ui";

import { headingVariantClassName } from "@/components/ui/heading";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DrawerSurfaceContext } from "@/components/ui/drawerSurfaceContext";
import { ArrowLeftIcon, XIcon } from "lucide-react";

/** Insertion-ordered ids of open left/right drawers, for stacked nesting. */
const drawerStackIds: string[] = [];
const drawerStackListeners = new Set<() => void>();

function notifyDrawerStack(): void {
  for (const listener of drawerStackListeners) {
    listener();
  }
}

const SheetOpenContext = React.createContext(false);

type DrawerStackPosition = {
  /** 0 = first open drawer; higher = stacked in front. */
  nest: number;
  /** How many left/right drawers sit on top of this one. */
  coveredBy: number;
};

/**
 * Tracks left/right drawers that are actually in the DOM (open). Closed sheets
 * still render `SheetContent` in React, so we must not register those.
 */
function useDrawerStackPosition(enabled: boolean, node: HTMLElement | null): DrawerStackPosition {
  const idRef = React.useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = `drawer-${crypto.randomUUID()}`;
  }
  const id = idRef.current;
  const [position, setPosition] = React.useState<DrawerStackPosition>({
    nest: 0,
    coveredBy: 0,
  });

  React.useLayoutEffect(() => {
    if (!enabled || node == null) {
      setPosition({ nest: 0, coveredBy: 0 });
      return;
    }
    drawerStackIds.push(id);
    const sync = (): void => {
      const index = drawerStackIds.indexOf(id);
      if (index < 0) {
        setPosition({ nest: 0, coveredBy: 0 });
        return;
      }
      setPosition({
        nest: index,
        coveredBy: Math.max(0, drawerStackIds.length - 1 - index),
      });
    };
    drawerStackListeners.add(sync);
    notifyDrawerStack();
    return () => {
      drawerStackListeners.delete(sync);
      const index = drawerStackIds.indexOf(id);
      if (index >= 0) {
        drawerStackIds.splice(index, 1);
      }
      notifyDrawerStack();
    };
  }, [enabled, id, node]);

  return enabled && node != null ? position : { nest: 0, coveredBy: 0 };
}

/** Covered drawers slide left (right-side) so a strip peeks; front drawer keeps lg/md. */
const coveredDrawerClassName =
  "lg:data-[side=right]:-translate-x-[calc(var(--drawer-cover-depth)*2rem)] lg:data-[side=left]:translate-x-[calc(var(--drawer-cover-depth)*2rem)]";

/** Nested stack slide — interpolates --drawer-cover-depth as the front drawer closes. */
const drawerStackMotionClassName =
  "lg:transition-[transform,--drawer-cover-depth] lg:duration-300 lg:ease-[cubic-bezier(0.32,0.72,0,1)]";

/** Right/left drawers: full viewport on mobile; inset panel from lg up. */
const sheetDrawerEdgeClassName =
  "max-lg:data-[side=left]:!top-0 max-lg:data-[side=left]:!right-0 max-lg:data-[side=left]:!bottom-0 max-lg:data-[side=left]:!left-0 max-lg:data-[side=left]:!h-dvh max-lg:data-[side=left]:!max-h-dvh max-lg:data-[side=left]:!w-full max-lg:data-[side=left]:!rounded-none max-lg:data-[side=left]:!border-0 max-lg:data-[side=right]:!top-0 max-lg:data-[side=right]:!right-0 max-lg:data-[side=right]:!bottom-0 max-lg:data-[side=right]:!left-0 max-lg:data-[side=right]:!h-dvh max-lg:data-[side=right]:!max-h-dvh max-lg:data-[side=right]:!w-full max-lg:data-[side=right]:!rounded-none max-lg:data-[side=right]:!border-0";

function Sheet({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? Boolean(open) : uncontrolledOpen;

  function handleOpenChange(next: boolean): void {
    if (!isControlled) {
      setUncontrolledOpen(next);
    }
    onOpenChange?.(next);
  }

  return (
    <SheetOpenContext.Provider value={isOpen}>
      <SheetPrimitive.Root
        data-slot="sheet"
        {...props}
        open={isOpen}
        onOpenChange={handleOpenChange}
      />
    </SheetOpenContext.Provider>
  );
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  overlayClassName,
  children,
  side = "right",
  showCloseButton = false,
  style,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  /** Floating close control — prefer {@link SheetHeader} `showClose` for drawers. */
  showCloseButton?: boolean;
  overlayClassName?: string;
}) {
  const isOpen = React.useContext(SheetOpenContext);
  const [node, setNode] = React.useState<HTMLElement | null>(null);
  const { nest, coveredBy } = useDrawerStackPosition(
    isOpen && (side === "right" || side === "left"),
    node,
  );

  return (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      <SheetPrimitive.Content
        {...props}
        ref={setNode}
        data-slot="sheet-content"
        data-side={side}
        data-drawer-nest={nest}
        data-drawer-covered-by={coveredBy}
        style={
          {
            ...style,
            "--drawer-cover-depth": coveredBy,
          } as React.CSSProperties
        }
        className={cn(
          "fixed z-50 flex flex-col gap-4 overflow-hidden bg-background bg-clip-padding text-sm text-foreground shadow-lg data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=left]:top-3 data-[side=left]:bottom-3 data-[side=left]:left-3 data-[side=left]:right-3 data-[side=left]:h-[calc(100dvh-1.5rem)] data-[side=left]:w-auto data-[side=left]:rounded-xl data-[side=left]:border data-[side=left]:border-border sm:data-[side=left]:right-auto sm:data-[side=left]:w-3/4 lg:data-[side=left]:!right-auto data-[side=right]:top-3 data-[side=right]:bottom-3 data-[side=right]:right-3 data-[side=right]:left-3 data-[side=right]:h-[calc(100dvh-1.5rem)] data-[side=right]:w-auto data-[side=right]:rounded-xl data-[side=right]:border data-[side=right]:border-border sm:data-[side=right]:left-auto sm:data-[side=right]:w-3/4 lg:data-[side=right]:!left-auto data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-open:animate-in data-open:fade-in-0 data-[side=bottom]:data-open:slide-in-from-bottom-10 data-[side=left]:data-open:slide-in-from-left-10 data-[side=right]:data-open:slide-in-from-right-10 data-[side=top]:data-open:slide-in-from-top-10 data-closed:animate-out data-closed:fade-out-0 data-[side=bottom]:data-closed:slide-out-to-bottom-10 data-[side=left]:data-closed:slide-out-to-left-10 data-[side=right]:data-closed:slide-out-to-right-10 data-[side=top]:data-closed:slide-out-to-top-10",
          sheetDrawerEdgeClassName,
          drawerStackMotionClassName,
          coveredDrawerClassName,
          className,
        )}
      >
        <DrawerSurfaceContext.Provider value={node}>
          {children}
          {showCloseButton ? (
            <SheetPrimitive.Close data-slot="sheet-close" asChild>
              <Button variant="ghost" className="absolute top-3 right-3" size="icon-sm">
                <XIcon />
                <span className="sr-only">Close</span>
              </Button>
            </SheetPrimitive.Close>
          ) : null}
        </DrawerSurfaceContext.Provider>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

type SheetHeaderProps = React.ComponentProps<"div"> & {
  showClose?: boolean;
  closeLabel?: string;
  /**
   * On mobile, replace the top-right X with a top-left back arrow (native push
   * pattern). Desktop keeps the X. Use for full-screen detail drawers.
   */
  mobileBack?: boolean;
  backLabel?: string;
};

function SheetHeader({
  className,
  children,
  showClose = true,
  closeLabel = "Close",
  mobileBack = false,
  backLabel = "Back",
  ...props
}: SheetHeaderProps) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "flex shrink-0 flex-row items-center gap-2 border-b border-border px-4 py-3",
        className,
      )}
      {...props}
    >
      {mobileBack ? (
        <SheetClose asChild>
          <Button type="button" variant="ghost" size="icon-sm" className="-ml-1 shrink-0 lg:hidden">
            <ArrowLeftIcon className="size-4" aria-hidden />
            <span className="sr-only">{backLabel}</span>
          </Button>
        </SheetClose>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</div>
      {showClose ? (
        <SheetClose asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={cn("shrink-0", mobileBack && "max-lg:hidden")}
          >
            <XIcon className="size-4" aria-hidden />
            <span className="sr-only">{closeLabel}</span>
          </Button>
        </SheetClose>
      ) : null}
    </div>
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        "mt-auto flex shrink-0 flex-row gap-2 border-t border-border p-4 max-lg:pb-[max(1rem,env(safe-area-inset-bottom))] max-sm:[&>[data-slot=button]]:min-w-0 max-sm:[&>[data-slot=button]]:flex-1",
        className,
      )}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(headingVariantClassName.drawer, "min-w-0 truncate text-left", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
